const normalizeText = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const COUNTRY_ALIASES = {
  'united states': ['usa', 'us', 'united states of america', 'america'],
  'united kingdom': ['uk', 'great britain', 'britain', 'england'],
  'united arab emirates': ['uae', 'emirates'],
  'russian federation': ['russia'],
  'south korea': ['korea', 'republic of korea'],
  'north korea': ['dprk', 'democratic peoples republic of korea'],
  'czech republic': ['czechia'],
  vietnam: ['viet nam'],
};

const STATE_ALIASES = {
  odisha: ['orissa'],
  uttarakhand: ['uttaranchal'],
  'jammu and kashmir': ['jammu kashmir', 'j&k'],
  'dadra and nagar haveli and daman and diu': ['daman and diu', 'dadra and nagar haveli'],
};

const CITY_ALIASES = {
  bengaluru: ['bangalore'],
  mumbai: ['bombay'],
  kolkata: ['calcutta'],
  chennai: ['madras'],
  kochi: ['cochin'],
  thiruvananthapuram: ['trivandrum'],
  vadodara: ['baroda'],
  prayagraj: ['allahabad'],
  gurugram: ['gurgaon'],
  hubballi: ['hubli'],
  shivamogga: ['shimoga'],
  vijayapura: ['bijapur'],
  belagavi: ['belgaum'],
  mysuru: ['mysore'],
};

const getAliases = (type, label) => {
  const key = normalizeText(label);
  if (!key) return [];
  if (type === 'country') return COUNTRY_ALIASES[key] || [];
  if (type === 'state') return STATE_ALIASES[key] || [];
  if (type === 'city') return CITY_ALIASES[key] || [];
  return [];
};

const getSearchTerms = (option, { type, getLabel, getCode }) => {
  const label = getLabel(option);
  const code = getCode ? getCode(option) : '';
  const aliases = getAliases(type, label);
  return [label, code, ...aliases].map(normalizeText).filter(Boolean);
};

const scoreOption = (labelNorm, terms, queryNorm) => {
  if (labelNorm === queryNorm) return 0;
  if (labelNorm.startsWith(queryNorm)) return 1;
  if (labelNorm.includes(queryNorm)) return 2;
  if (terms.some((term) => term.startsWith(queryNorm))) return 3;
  return 4;
};

export const filterLocationOptions = (
  options,
  state,
  { type, getLabel = (option) => option, getCode = null }
) => {
  const inputValue = state?.inputValue || '';
  const queryNorm = normalizeText(inputValue);
  if (!queryNorm) return options;

  const tokens = queryNorm.split(' ').filter(Boolean);
  if (!tokens.length) return options;

  return options
    .map((option) => {
      const terms = getSearchTerms(option, { type, getLabel, getCode });
      if (!terms.length) return null;
      const matched = tokens.every((token) => terms.some((term) => term.includes(token)));
      if (!matched) return null;

      const labelNorm = normalizeText(getLabel(option));
      return {
        option,
        score: scoreOption(labelNorm, terms, queryNorm),
        labelNorm,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (a.score !== b.score) return a.score - b.score;
      return a.labelNorm.localeCompare(b.labelNorm);
    })
    .map((entry) => entry.option);
};

export const countryFilterOptions = (options, state) =>
  filterLocationOptions(options, state, {
    type: 'country',
    getLabel: (option) => option?.name || '',
    getCode: (option) => option?.code || '',
  });

export const stateFilterOptions = (options, state) =>
  filterLocationOptions(options, state, {
    type: 'state',
    getLabel: (option) => option?.name || '',
    getCode: (option) => option?.code || '',
  });

export const cityFilterOptions = (options, state) =>
  filterLocationOptions(options, state, {
    type: 'city',
    getLabel: (option) => (typeof option === 'string' ? option : option?.name || ''),
    getCode: () => '',
  });
